/* eslint-disable max-lines */
import React, { useCallback, useContext, useMemo } from "react";
// components
import HeaderCTA from "@cart-client/components/review-item-list-wrapper/components/HeaderCTA";
import ItemCounter from "@cart-client/components/review-item-list-wrapper/components/ItemCounter";
import DeliveryTagTitleAndSubtitle from "@cart-client/components/review-item-list-wrapper/components/DeliveryTagTitleAndSubtitle";
import { CartItemActions } from "@cart-client/components/cart-items-actions";
import { IMRestrictedItemSeperatorNode } from "@cart-client/components/review-item-list-wrapper/components/IMRestrictedItemSeperatorNode";
// contexts
import { CartContext } from "@cart-client/contexts/CartContext";
// types
import { GiftingCartState } from "@cart-client/reducers/GiftingCartReducer";
import {
    ReviewItemListCardProps,
    Item,
    CTAType,
    ItemSection,
} from "@swiggy-private/dls-web-recipe-common-web/dist/components/ReviewItemListCard/types";
import {
    CartItem,
    CombinedCartInStockItemRender,
    IMStructureItem,
    FoodCartItemWithUUID,
    IMCartItemWithUUID,
    IMGiftBagItemWithUUID,
    GiftingCartItemType,
} from "@swiggy-private/cart-types";
// hooks
import { useGiftCartItemActions } from "@cart-client/hooks/useGiftCartItemActions";
import { useIMGiftBagHalfCardSelection } from "@cart-client/hooks/useIMGiftBagHalfCardSelection";
import { useGetReviewItemListAnalytics } from "@cart-client/components/review-item-list-wrapper/hooks/useGetReviewItemListAnalytics";
import { useGiftingCartErrorCrouton } from "./useGiftingCartError/useGiftingCartErrorCrouton";
// utils
import { sortSubCartsBySLA, calcQuantityCountForItems } from "@cart-client/helpers/GiftingCart";
import {
    buildFoodItemToRenderProps,
    segregateFoodItemByShipment,
    isFoodSubCart,
    isFoodItem,
} from "@cart-client/helpers/GiftingFoodSubCart";
import {
    buildIMItemToRenderProps,
    segregateIMItemByShipmentId,
    buildIMGiftBagItemToRenderProps,
    isIMSubCart,
    isAnyIMItem,
    isIMGiftBagItem,
} from "@cart-client/helpers/GiftingIMSubCart";
import { format, isEmpty } from "@swiggy-private/core-ui";
// constants
import { STATIC_TEXTS } from "@cart-client/constants/GiftingCart";
import { isItemAvailable } from "@swiggy-private/cart-ui";

interface UseGiftingSubCartBuilderProps {
    foodCartItemList?: FoodCartItemWithUUID[];
    imCartItemList?: IMCartItemWithUUID[];
    imGiftBagItemList?: IMGiftBagItemWithUUID[];
}

export const useGiftingSubCartBuilder = ({
    foodCartItemList,
    imCartItemList,
    imGiftBagItemList,
}: UseGiftingSubCartBuilderProps) => {
    const { cartState } = useContext(CartContext);
    const { reviewedCart, localCart } = cartState as GiftingCartState;
    const analyticsEvents = useGetReviewItemListAnalytics({ reviewedCart: reviewedCart?.data });
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { foodCartItems, imShipmentDetails, imGiftBag, restaurant_details } = reviewedCart?.data || {};
    const { name: restaurantName = "", disclaimer = "" } = restaurant_details || {};
    // use gift cart item actions hook to update the quantity of an item in the cart
    const { onItemUpdate, onRemoveOOSItems } = useGiftCartItemActions({
        foodCartItemList: foodCartItemList || [],
        imCartItemList: imCartItemList || [],
    });
    const { onGiftBagAddition, onGiftBagRemoval, giftBagSelectionHalfCardProps } = useIMGiftBagHalfCardSelection({
        foodCartItemList: foodCartItemList || [],
        imCartItemList: imCartItemList || [],
    });
    // use gifting cart error crouton hook to validate item max quantity addition and render error crouton
    const { validateItemMaxQuantityAdditionAndRenderErrorCrouton } = useGiftingCartErrorCrouton();

    // remove oos/unserviceable subcarts from the cart
    const onRemoveOOSFromCartCallback = useCallback(
        (cart: CombinedCartInStockItemRender) => {
            if (isFoodSubCart(cart)) {
                onRemoveOOSItems({ foodItemsToRemove: cart.items as CartItem[] });
            } else if (isIMSubCart(cart)) {
                onRemoveOOSItems({
                    imItemsToRemove: cart.items as IMStructureItem[],
                });
            }
        },
        [onRemoveOOSItems],
    );

    const findCartItem = useCallback(
        (item: Item) => {
            if (isFoodItem(item?.subType as GiftingCartItemType)) {
                return foodCartItemList?.find((foodItem) => String(foodItem.id_hash) === String(item.id));
            }
            if (isAnyIMItem(item?.subType as GiftingCartItemType)) {
                return imCartItemList?.find((imItem) => String(imItem.itemId) === String(item.id));
            }
            return null;
        },
        [foodCartItemList, imCartItemList],
    );

    const onQuantityChange = useCallback(
        (item: Item, ctaType: CTAType) => {
            const itemFromList = findCartItem(item);
            if (isEmpty(itemFromList)) {
                return;
            }

            if (ctaType === CTAType.INCREMENT) {
                analyticsEvents.clickQuantityIncEvent({ item });
                const isItemMaxQuantityAdditionValid = validateItemMaxQuantityAdditionAndRenderErrorCrouton(item);
                if (!isItemMaxQuantityAdditionValid) {
                    return;
                }
                itemFromList.quantity += 1;
            } else if (ctaType === CTAType.DECREMENT && itemFromList.quantity > 0) {
                analyticsEvents.clickQuantityDecEvent({ item });
                itemFromList.quantity -= 1;
            }

            onItemUpdate({
                item: itemFromList,
                itemSubType: item.subType as GiftingCartItemType,
            });
        },
        [analyticsEvents, findCartItem, onItemUpdate, validateItemMaxQuantityAdditionAndRenderErrorCrouton],
    );

    /**
     * Build props for In Stock IM and Food Carts
     * Organise food and im item carts based on SLA Time
     */
    const inStockFoodItems = useMemo(() => foodCartItemList?.filter((item) => isItemAvailable(item)), [foodCartItemList]);
    const inStockIMItems = useMemo(() => imCartItemList?.filter((item) => item.outOfStock !== true), [imCartItemList]);
    const inStockIMGiftBagItems = useMemo(
        () => imGiftBagItemList?.filter((item) => item.available === true),
        [imGiftBagItemList],
    );

    /**
     * Segregate food and im items based on shipment
     */
    const foodAvailItemsSubCart = useMemo(() => {
        return (
            segregateFoodItemByShipment({
                isServiceable: foodCartItems?.isServiceable ?? false,
                slaTime: foodCartItems?.slaTime ?? 0,
                inStockFoodItems: inStockFoodItems,
                disclaimer: disclaimer,
            }) || []
        );
    }, [inStockFoodItems, foodCartItems, disclaimer]);

    const imAvailItemsSubCarts = useMemo(() => {
        return (
            segregateIMItemByShipmentId({
                items: inStockIMItems,
                shipments: imShipmentDetails,
                giftBagItems: inStockIMGiftBagItems,
            }) || []
        );
    }, [inStockIMItems, imShipmentDetails, inStockIMGiftBagItems]);

    // food cart will only be a single subcart, IM subcarts can be multiple, we need to organise them based on SLA Time with lower slaTime first
    const allSubCarts = useMemo(() => {
        const imCartSortedBySLA = sortSubCartsBySLA(imAvailItemsSubCarts as CombinedCartInStockItemRender[]);
        const allCarts = [...imCartSortedBySLA, foodAvailItemsSubCart as CombinedCartInStockItemRender];
        return allCarts;
    }, [foodAvailItemsSubCart, imAvailItemsSubCarts]);

    const onItemAddition = useCallback(
        async (item: Item) => {
            analyticsEvents.clickAddToCartEvent({ item });
            if (isIMGiftBagItem(item.subType as GiftingCartItemType)) {
                await onGiftBagAddition({ allSubCarts, item });
                return;
            }
        },
        [allSubCarts, analyticsEvents, onGiftBagAddition],
    );

    const onItemRemoval = useCallback(
        async (item: Item) => {
            analyticsEvents.clickRemoveFromCartEvent({ item });
            if (isIMGiftBagItem(item.subType as GiftingCartItemType)) {
                await onGiftBagRemoval({ item });
                return;
            }
        },
        [onGiftBagRemoval, analyticsEvents],
    );

    const onItemAction = useCallback(
        (item: Item, ctaType: CTAType) => {
            switch (ctaType) {
                case CTAType.INCREMENT:
                case CTAType.DECREMENT:
                    onQuantityChange(item, ctaType);
                    break;
                case CTAType.OOS:
                    break;
                case CTAType.ADD:
                    onItemAddition(item);
                    break;
                case CTAType.REMOVE:
                    onItemRemoval(item);
                    break;
            }
        },
        [onQuantityChange, onItemAddition, onItemRemoval],
    );

    const cartItemImpressionCallback = useCallback(
        (item: Item) => {
            analyticsEvents.impressionItemEvent({ item });
        },
        [analyticsEvents],
    );

    const getHeaderText = useCallback(
        (cart: CombinedCartInStockItemRender, idx: number) => {
            const filteredSubCarts = allSubCarts.filter((subCart) => !isEmpty(subCart));
            if (isFoodSubCart(cart) && filteredSubCarts.length > 1) {
                return format(STATIC_TEXTS.RESTAURANT_MULTI_DELIVERY, String(idx + 1), restaurantName);
            }
            if (isIMSubCart(cart)) {
                if (filteredSubCarts.length === 1) {
                    return format(STATIC_TEXTS.GIFT_STORE_SINGLE_DELIVERY);
                }
                return format(STATIC_TEXTS.GIFT_STORE_MULTI_DELIVERY, String(idx + 1));
            }
            return format(STATIC_TEXTS.RESTAURANT_SINGLE_DELIVERY, restaurantName);
        },
        [allSubCarts, restaurantName],
    );

    const buildItemSections = useCallback(
        (cart: CombinedCartInStockItemRender) => {
            const itemSections: ItemSection[] = [];
            const propsBuilder = isFoodSubCart(cart) ? buildFoodItemToRenderProps : buildIMItemToRenderProps;
            const itemsToRender = propsBuilder({
                parentCartId: cart.subCartId,
                items: cart.items as CartItem[] & IMStructureItem[],
                onItemAction,
                impressionCallback: cartItemImpressionCallback,
            });
            const { items = [], restrictedQuantityItems = [] } = itemsToRender || {};
            const itemCount = calcQuantityCountForItems(items) + calcQuantityCountForItems(restrictedQuantityItems);

            if (isIMSubCart(cart)) {
                let imGiftBagAndOtherItems = [...items];
                if (!isEmpty(cart.giftBagItems) && !isEmpty(imGiftBag?.widgetInfo?.title)) {
                    const giftBagItemsToRender = buildIMGiftBagItemToRenderProps({
                        parentCartId: cart.subCartId,
                        items: cart.giftBagItems,
                        onItemAction,
                        title: imGiftBag?.widgetInfo?.title,
                        description: imGiftBag?.widgetInfo?.description,
                        creativeId: imGiftBag?.widgetInfo?.creativeId,
                        impressionCallback: cartItemImpressionCallback,
                    });
                    imGiftBagAndOtherItems = [...(giftBagItemsToRender || []), ...imGiftBagAndOtherItems];
                }
                if (!isEmpty(imGiftBagAndOtherItems)) {
                    itemSections.push({ items: imGiftBagAndOtherItems });
                }
                if (!isEmpty(restrictedQuantityItems)) {
                    itemSections.push({
                        seperatorHeaderNode: <IMRestrictedItemSeperatorNode />,
                        items: restrictedQuantityItems,
                    });
                }
            } else if (isFoodSubCart(cart) && !isEmpty(items)) {
                itemSections.push({ items });
            }

            return { itemSections, itemCount };
        },
        [
            cartItemImpressionCallback,
            imGiftBag?.widgetInfo?.creativeId,
            imGiftBag?.widgetInfo?.description,
            imGiftBag?.widgetInfo?.title,
            onItemAction,
        ],
    );

    const propsForAvailItemListCart = useCallback(
        (cart: CombinedCartInStockItemRender, idx: number) => {
            if (isEmpty(cart?.items)) {
                return null;
            }

            const headerText = getHeaderText(cart, idx);
            const { itemSections, itemCount } = buildItemSections(cart);
            const slaTimeText = cart.slaTimeString || "";
            const slaTagImage = cart.slaTagImage || "";

            const cartImpressionCallback = () => {
                analyticsEvents.impressionCartEvent(cart);
            };

            return {
                header: {
                    leftSection: {
                        text: (
                            <DeliveryTagTitleAndSubtitle
                                title={headerText}
                                slaTime={slaTimeText}
                                deliveryTagImage={slaTagImage}
                                isUnserviceable={!cart.isServiceable}
                            />
                        ),
                    },
                    rightSection: {
                        text: cart.isServiceable ? (
                            <ItemCounter count={itemCount} />
                        ) : (
                            <HeaderCTA onClick={() => onRemoveOOSFromCartCallback(cart)} />
                        ),
                    },
                },
                itemRenderer: {
                    disableInteraction: !cart.isServiceable,
                    sections: itemSections,
                },
                footer: {
                    disableInteraction: !cart.isServiceable,
                    footerNode: isFoodSubCart(cart) ? (
                        <CartItemActions
                            disableRedirectionToMenu={true}
                            specialInstruction={localCart?.orderComment ?? ""}
                            isCroutonVisible={false}
                            isBulkOrderFn={false}
                            cartResponse={reviewedCart}
                            goToMenu={() => {
                                //no-op
                            }}
                            isPopCart={false}
                        />
                    ) : null,
                },
                impressionEventCallback: cartImpressionCallback,
                disclaimer: cart?.disclaimer || "",
            } as ReviewItemListCardProps;
        },
        [
            getHeaderText,
            buildItemSections,
            localCart?.orderComment,
            reviewedCart,
            analyticsEvents,
            onRemoveOOSFromCartCallback,
        ],
    );

    const propsForAllInStockItemsCarts = useMemo(() => {
        const allCartProps = allSubCarts.map((cart, idx) => {
            return propsForAvailItemListCart(cart as CombinedCartInStockItemRender, idx);
        });
        return allCartProps.filter((cart) => !isEmpty(cart)) as ReviewItemListCardProps[];
    }, [allSubCarts, propsForAvailItemListCart]);

    return {
        propsForAllInStockItemsCarts,
        availItemCount: (inStockFoodItems?.length || 0) + (inStockIMItems?.length || 0),
        giftBagSelectionHalfCardProps,
        onRemoveOOSFromCartCallback,
    };
};
